import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { WorkflowStepBatchConfigSchema } from "../../types/batch";
import {
  OPTIONAL_QUANTITY_INPUT,
  OPTIONAL_TEXT_INPUT,
} from "../procurement/optionalInputs";

/**
 * Upsert Product
 *
 * Downstream of the "Observe DataSource Link" trigger. Takes ONE item's fields
 * as single-value input sockets. The trigger emits a per-field `array<T>` per
 * column plus `metadataId`, and a single (shared) `dataSourceLink`; the engine's
 * lockstep fanout zips the per-field arrays into one item per row (broadcasting
 * the shared `dataSourceLink`). With a `batch` config on this step the per-item
 * handler runs in chunks inside `executeStepBatch` instead of one child workflow
 * per row.
 *
 * For each item it UPSERTS a ProductV3 by its caller-provided `key` (its string
 * primary key) — creating the product or updating its display fields
 * (displaySku/displayDescription/upc/isSellable/isActive/weight) — and links the product to
 * the row's surrogate key via ProductV3DataLink (deduped on productKey +
 * dataSourceLinkId + dataId). A leaf step (no outgoing data edges) — the
 * prerequisite for fanout batching.
 */
export const upsertProductStep = defineStepConfig({
  type: "upsert_product",
  name: "Upsert Product",
  description:
    "Upserts one catalog product from an item's fields (key/displaySku/displayDescription/upc/isSellable/isActive + dataSourceLink/metadataId): a ProductV3 keyed on its explicit string `key` (create or update), a ProductV3DataLink to the row's surrogate key, and the registered UPC schema-extension column when `upc` is wired. Fan a trigger's per-field array outputs into its single-value inputs and add a `batch` config to process rows in chunks.",
  category: "Data Ingest",
  libraryModule: "Platform",

  // `.catchall(z.unknown())` is load-bearing: `executeStep` parses the incoming
  // input through this schema, and a strict `z.object` silently STRIPS every
  // dynamic `ext:<tableName>:<fieldName>` key — the writes would look wired and
  // land nothing. Which extensions contribute those sockets is not step config:
  // it's whatever is registered in `core_schema_extensions` for IM_PRODUCT_V3.
  inputSchema: z
    .object({
      key: z.string(),
      // dataSourceLink + metadataId sit directly under `key` so the row's identity
      // sockets group together at the top of the node, ahead of the display fields.
      dataSourceLink: z.object({
        id: z.string().uuid(),
        dataSourceId: z.string().uuid(),
        schemaName: z.string(),
        tableName: z.string(),
      }),
      metadataId: z.string(),
      /**
       * The row's CDC operation, when wired from an Observe trigger's
       * `changeOperation` output. A CDC delete stages its BEFORE image, so
       * without this the row is indistinguishable from an insert and this
       * upsert-only sink writes the deleted row back. Left unwired (or on the
       * initial backfill phase) every row is treated as an upsert.
       */
      changeOperation: z
        .enum(["insert", "update", "delete"])
        .optional()
        .describe(
          "CDC operation for this row. Wire from the Observe trigger's Change Operation output so deletes are not written back as upserts.",
        ),
      displaySku: z.string().nullable().optional(),
      displayDescription: z.string().nullable().optional(),
      /**
       * Product-level UPC written to the registered IM_PRODUCT_V3 schema
       * extension field named `upc`. Same durability as SKU/Description, but
       * stored in `schema_ext` rather than on `im_products_v3`. Unwired skips
       * the write; wire the ingest UPC (or IPAK+IMID+IITM concatenated) here.
       */
      upc: z.string().nullable().optional(),
      isSellable: z.boolean().optional(),
      isActive: z.boolean().optional(),
      /**
       * Per-unit item weight, in the unit wired to `weightUom` (store the
       * source's own value and unit; conversion happens on read). Unwired or
       * blank leaves the stored weight alone, so a feed without weights never
       * clears one another feed wrote.
       */
      weight: OPTIONAL_QUANTITY_INPUT.describe(
        "Per-unit item weight in the unit given by Weight UOM. Blank or unwired keeps the stored weight.",
      ),
      weightUom: OPTIONAL_TEXT_INPUT.describe(
        "Unit of the weight value, e.g. LB or KG. Required with Weight: a weight without a unit is skipped.",
      ),
    })
    .catchall(z.unknown()),

  outputSchema: z.object({
    written: z
      .boolean()
      .describe("The row's ProductV3 and ProductV3DataLink were upserted"),
    skipped: z
      .boolean()
      .describe("Row skipped — missing key/metadataId/dataSourceLink"),
    deleteSkipped: z
      .boolean()
      .describe(
        "Row was a CDC delete and was NOT applied. This sink only upserts, so it neither writes the deleted row back nor removes the product; the delete needs an explicit soft/hard-delete decision.",
      ),
    extensionWritten: z
      .boolean()
      .describe(
        "The row's registered-extension values were written (false when the entity has no provisioned extensions)",
      ),
  }),

  // This step is purpose-built for the batched observe→upsert backfill, so it
  // ships a default `batch` config. `batch.size` doubles as the Observe trigger's
  // initial-backfill window size (see resolveObserveWindowPlan): one window → one
  // `executeStepBatch` chunk → continue-as-new for the next window. Read by the
  // engine via `getBatchConfig(step.config)`, not as a normal config field.
  configSchema: z.object({
    batch: WorkflowStepBatchConfigSchema.prefault({ size: 500 }),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "30 minutes",
    retry: { maximumAttempts: 2 },
  },
});
